import { useState, MouseEvent } from 'react';
import { ButtonBase, Divider, Tooltip, Typography } from '@mui/material';
import { AppPaper, Flex } from '@/UI/styles';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { ShortcutPair, ShortcutPairSlotProps } from '@/UI/ShortcutPair/ShortcutPair';
import { NavigateIcon } from '@/UI/Icons/NavigateIcon';
import { UndoIcon } from '@/UI/Icons/UndoIcon';
import { RedoIcon } from '@/UI/Icons/RedoIcon';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { color } from '@/colors';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { HandIcon } from '@/UI/Icons/HandIcon';
import { getShortcutParts } from '@/utils/shortcuts';
import { ZoomMenu } from './ZoomMenu';

type Tool = 'select' | 'pan';

export interface ActionsBarProps {
  canUndo?: boolean;
  canRedo?: boolean;
  zoomPercentage: number;
  zoomLimitsPercentage: {
    min: number;
    max: number;
  };
  isEditMode: boolean;
  onUndoRedo?: (undoRedo: 'undo' | 'redo') => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  // onZoomChange: (zoom: number) => void;
  onZoomToHundred: () => void;
  onZoomToFit: () => void;
  isSelectMode: boolean;
  setIsSelectMode: (value: boolean) => void;
  isFullMode?: boolean;
  buttonSize?: number;
  paddingX?: string | number;
  paddingY?: string | number;
  height?: string | number;
  children?: React.ReactNode;
  noBorderLeft?: boolean;
}

const moveShortcut = getShortcutParts('MOVE');
const panShortcut = getShortcutParts('PAN');
const undoShortcut = getShortcutParts('UNDO');
const redoShortcut = getShortcutParts('REDO');

export const ActionsBar = ({
  canUndo,
  canRedo,
  zoomPercentage,
  // zoomLimitsPercentage,
  isEditMode,
  onUndoRedo,
  onZoomIn,
  onZoomOut,
  onZoomToHundred,
  onZoomToFit,
  isSelectMode,
  setIsSelectMode,
  isFullMode,
  buttonSize = 28,
  paddingX,
  paddingY,
  height,
  children,
  noBorderLeft,
}: ActionsBarProps) => {
  const activeTool: Tool = isSelectMode ? 'select' : 'pan';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleToolChange = (tool: Tool | null) => {
    setIsSelectMode(tool === 'select');
  };

  // const handleZoomChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.value === '') {
  //     setZoomValue(undefined);
  //   } else {
  //     setZoomValue(e.target.value);
  //   }
  // };

  // const handleZoomBlur = (e: FocusEvent<HTMLInputElement>) => {
  //   const valueWithoutPercent = e.target.value.replace('%', '');
  //   const zoomValue = parseInt(valueWithoutPercent, 10);

  //   if (!isLegalValue(zoomValue)) {
  //     setZoomValue(`${zoomPercentage}%`);
  //   } else {
  //     onZoomChange?.(zoomValue);
  //   }
  // };

  // const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
  //   const valueWithoutPercent = (e.target as HTMLInputElement).value.replace('%', '');
  //   const res = Number(valueWithoutPercent);
  //   const increment = e.shiftKey ? 10 : 1;

  //   if (e.key === 'ArrowUp') {
  //     const newZoom = res + increment;
  //     onZoomChange?.(Math.min(newZoom, zoomLimitsPercentage.max));
  //   } else if (e.key === 'ArrowDown') {
  //     const newZoom = res - increment;
  //     onZoomChange?.(Math.max(newZoom, zoomLimitsPercentage.min));
  //   }
  // };

  const handleOpenZoomMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleCloseZoomMenu = () => setAnchorEl(null);

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    borderLeft: noBorderLeft ? 'none' : `1px solid ${color.White04_T}`,
    ...(isFullMode
      ? {
          height: height ?? '60px',
          px: paddingX ?? '20px',
          py: paddingY ?? '15px',
          borderRadius: 0,
        }
      : {
          height: height ?? '44px',
          py: paddingY ?? 1.25,
          px: paddingX ?? 1,
        }),
  };

  return (
    <AppPaper sx={containerStyle}>
      {/* Left controls */}
      {children}
      {/* Design Tools (right side) */}
      <AppToggleButtons
        value={activeTool}
        options={[
          {
            value: 'select',
            label: <NavigateIcon />,
            'aria-label': 'Navigate',
            tooltipText: <ShortcutPair label="Move" shortcut={moveShortcut} />,
          },
          {
            value: 'pan',
            label: <HandIcon />,
            'aria-label': 'Pan',
            tooltipText: <ShortcutPair label="Pan" shortcut={panShortcut} />,
          },
        ]}
        onChange={handleToolChange}
        mode="light"
        btnW={buttonSize}
        btnH={buttonSize}
        isIcons
        tooltipPlacement="top"
        tooltipSlotProps={ShortcutPairSlotProps}
      />
      {isEditMode && onUndoRedo && (
        <>
          <Divider orientation="vertical" flexItem sx={{ backgroundColor: color.White04_T }} />
          <Flex sx={{ gap: 0.25 }}>
            <Tooltip
              title={<ShortcutPair label="Undo" shortcut={undoShortcut} />}
              placement="top"
              enterDelay={800}
              enterNextDelay={800}
              slotProps={ShortcutPairSlotProps}
            >
              {/* span needed so Tooltip works when button is disabled (pointer-events: none) */}
              <span>
                <AppIconButton
                  onClick={() => onUndoRedo('undo')}
                  disabled={!canUndo}
                  width={buttonSize}
                  height={buttonSize}
                >
                  <UndoIcon />
                </AppIconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={<ShortcutPair label="Redo" shortcut={redoShortcut} />}
              placement="top"
              enterDelay={800}
              enterNextDelay={800}
              slotProps={ShortcutPairSlotProps}
            >
              {/* span needed so Tooltip works when button is disabled (pointer-events: none) */}
              <span>
                <AppIconButton
                  onClick={() => onUndoRedo('redo')}
                  disabled={!canRedo}
                  width={buttonSize}
                  height={buttonSize}
                >
                  <RedoIcon />
                </AppIconButton>
              </span>
            </Tooltip>
          </Flex>
        </>
      )}
      <Divider orientation="vertical" flexItem sx={{ backgroundColor: color.White04_T }} />
      <ButtonBase
        onClick={handleOpenZoomMenu}
        sx={{ gap: 1, bgcolor: anchorEl ? color.Black84 : 'transparent', py: 0.5, pl: 1, pr: 0.75, borderRadius: 1 }}
      >
        <Typography component="div" sx={{ width: '32px', display: 'inline-flex' }} variant="body-sm-rg">
          {`${zoomPercentage}%`}
        </Typography>
        <CaretIcon />
      </ButtonBase>
      {/* Decided to not allow editing. Not removing now in case we want to revive it */}
      {/* <Input
        size="small"
        value={zoomValue ?? ''}
        onChange={handleZoomChange}
        onBlur={handleZoomBlur}
        onKeyDown={handleKeyDown}
        inputProps={{
          min: zoomLimitsPercentage.min,
          max: zoomLimitsPercentage.max,
        }}
        endAdornment={
          <InputAdornment position="end" sx={{ cursor: 'pointer' }} onClick={handleOpenZoomMenu}>
            <CaretIcon />
          </InputAdornment>
        }
        sx={{
          
          pointerEvents: 'none',
          width: '80px',
          '&.MuiInputBase-root': {
            border: 'none',
            bgcolor: anchorEl ? color.Black84 : 'transparent',
          },
        }}
      /> */}
      <ZoomMenu
        isOpen={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseZoomMenu}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onZoomToFit={onZoomToFit}
        onZoomToHundred={onZoomToHundred}
      />
    </AppPaper>
  );
};
